/**
 * 📤 Employee Export Service
 * Handles employee data export to Excel and CSV formats
 */

import { apiFetch } from "../../../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

/**
 * Helper function to pick the first valid value from a list
 * Filters out null, undefined, empty strings, and "N/A"
 */
const pickExportValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed === "N/A" || trimmed === "NA") continue;
      return value;
    }
    return value;
  }
  return "N/A";
};

/**
 * Prepare employee data for export with all details
 * @param {Array} employees - Array of employee objects
 * @returns {Array} Formatted export data
 */
export const prepareExportData = (employees) => {
  return employees.map((employee) => ({
    "Employee ID": employee.id,
    "Full Name": employee.name,
    Email: employee.email,
    Phone: employee.phone,
    Position: employee.position,
    Department: employee.employeeType || employee.department,
    Company: employee.company,
    Status: employee.status,
    Salary: employee.salary,
    "Join Date": employee.joinDate
      ? new Date(employee.joinDate).toLocaleDateString()
      : "N/A",
    "Arabic Name": pickExportValue(
      employee.fullEmployeeData?.arabicName,
      employee.arabicName,
      employee.fullEmployeeData?.nameArabic,
      employee.nameArabic
    ),
    "National ID": employee.fullEmployeeData?.nationalId || employee.nationalId || "N/A",
    "Date of Birth": employee.fullEmployeeData?.dateOfBirth || employee.dateOfBirth
      ? new Date(employee.fullEmployeeData?.dateOfBirth || employee.dateOfBirth).toLocaleDateString()
      : "N/A",
    Age: employee.fullEmployeeData?.age || employee.age || "N/A",
    Gender: employee.fullEmployeeData?.gender || employee.gender || "N/A",
    "Emergency Phone": employee.fullEmployeeData?.emergencyPhone || employee.emergencyPhone || "N/A",
    Address: pickExportValue(
      employee.fullEmployeeData?.address,
      employee.address,
      employee.fullEmployeeData?.arabicAddress,
      employee.arabicAddress
    ),
    City: employee.fullEmployeeData?.city || employee.city || "N/A",
    Country: employee.fullEmployeeData?.country || employee.country || "N/A",
    Nationality: employee.fullEmployeeData?.nationality || employee.nationality || "N/A",
    "Marital Status": employee.fullEmployeeData?.maritalStatus || employee.maritalStatus || "N/A",
    Religion: employee.fullEmployeeData?.religion || employee.religion || "N/A",
    "Client Name": employee.fullEmployeeData?.clientName || employee.clientName || "N/A",
    "Client Code": employee.fullEmployeeData?.clientCode || employee.clientCode || "N/A",
    "Working Age Years": employee.fullEmployeeData?.workingAgeYears || employee.workingAgeYears || 0,
    "Working Age Months": employee.fullEmployeeData?.workingAgeMonths || employee.workingAgeMonths || 0,
    "Labor Mode": employee.fullEmployeeData?.laborMode || employee.laborMode || "N/A",
    "Contract Date": employee.fullEmployeeData?.contractDate || employee.contractDate
      ? new Date(employee.fullEmployeeData?.contractDate || employee.contractDate).toLocaleDateString()
      : "N/A",
    "Contract Expiration": employee.fullEmployeeData?.contractExpiration || employee.contractExpiration
      ? new Date(employee.fullEmployeeData?.contractExpiration || employee.contractExpiration).toLocaleDateString()
      : "N/A",
    "Term of Contract": employee.fullEmployeeData?.termOfContract || employee.termOfContract || "N/A",
    "Insurance Status": employee.fullEmployeeData?.insuranceStatus || employee.insuranceStatus || "N/A",
    "Insurance Number": employee.fullEmployeeData?.insuranceNumber || employee.insuranceNumber || "N/A",
    "Insurance Salary": employee.fullEmployeeData?.insuranceSalary || employee.insuranceSalary || 0,
    "Insurance Start Date": employee.fullEmployeeData?.insuredBeginningDate || employee.insuredBeginningDate
      ? new Date(employee.fullEmployeeData?.insuredBeginningDate || employee.insuredBeginningDate).toLocaleDateString()
      : "N/A",
    "Months for Insurance": employee.fullEmployeeData?.monthsForInsurance || employee.monthsForInsurance || 0,
    "Graduate Institution": employee.fullEmployeeData?.graduateInstitution || employee.graduateInstitution || "N/A",
    Major: employee.fullEmployeeData?.major || employee.major || "N/A",
    "Graduation Year": employee.fullEmployeeData?.graduationYear || employee.graduationYear || "N/A",
    "Created At": employee.fullEmployeeData?.createdAt || employee.createdAt
      ? new Date(employee.fullEmployeeData?.createdAt || employee.createdAt).toLocaleDateString()
      : "N/A",
    "Updated At": employee.fullEmployeeData?.updatedAt || employee.updatedAt
      ? new Date(employee.fullEmployeeData?.updatedAt || employee.updatedAt).toLocaleDateString()
      : "N/A",
  }));
};

/**
 * Generate filename for export based on filters and timestamp
 * @param {Object} filters - Filter criteria
 * @returns {string} Generated filename
 */
export const generateExportFilename = (filters = {}) => {
  let filename = "employees_filtered";
  if (filters.searchTerm) filename += `_search-${filters.searchTerm}`;
  if (filters.filterDepartment) filename += `_dept-${filters.filterDepartment}`;
  if (filters.filterStatus) filename += `_status-${filters.filterStatus}`;
  if (filters.filterCompany) filename += `_company-${filters.filterCompany}`;
  filename += `_${new Date().getTime()}`;
  return filename;
};

/**
 * Generate CSV content from employee data
 * @param {Array} employees - Array of employee objects
 * @returns {string} CSV content
 */
export const generateCSV = (employees) => {
  if (employees.length === 0) return "";

  // Define CSV headers
  const headers = [
    "Employee ID",
    "Full Name",
    "Email",
    "Phone",
    "Labor mode",
    "Department",
    "Company",
    "Status",
    "Salary",
    "Join Date",
  ];

  // Create CSV rows array starting with headers
  const csvRows = [headers.join(",")];

  // Add employee data rows
  employees.forEach((emp) => {
    const row = [
      emp.id,
      `"${emp.name}"`, // Wrap in quotes to handle commas in names
      emp.email,
      emp.phone,
      `"${emp.laborMode || emp.fullEmployeeData?.laborMode || "N/A"}"`,
      `"${emp.employeeType || emp.department || "N/A"}"`,
      `"${emp.company || "N/A"}"`,
      emp.status,
      emp.salary || 0,
      emp.joinDate ? new Date(emp.joinDate).toLocaleDateString() : "N/A",
    ];
    csvRows.push(row.join(","));
  });

  // Return CSV content as string
  return csvRows.join("\n");
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - Filename without extension
 */
export const downloadCSV = (csvContent, filename) => {
  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  // Add to DOM, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download blob as Excel file
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename without extension
 */
export const downloadExcel = (blob, filename) => {
  // Create blob URL for download
  const url = window.URL.createObjectURL(blob);
  // Create download link element
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${filename}.xlsx`);
  // Add link to DOM and trigger click
  document.body.appendChild(link);
  link.click();
  // Clean up
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Export employees to Excel via API
 * @param {Array} employees - Array of employee objects
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Blob>} Excel file blob
 */
export const exportToExcelAPI = async (employees, filters = {}) => {
  const exportData = prepareExportData(employees);
  
  const response = await apiFetch(`${API_URL}/employees/export-filtered`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      data: exportData,
      filters: {
        ...filters,
        totalRecords: employees.length,
      },
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to export employees");
  }

  // Get blob from response
  return await response.blob();
};

/**
 * Export employees to Excel (with CSV fallback)
 * @param {Array} employees - Array of employee objects
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Export result with success status and metadata
 */
export const exportEmployees = async (employees, filters = {}) => {
  const filename = generateExportFilename(filters);
  
  try {
    // Try Excel export first
    const blob = await exportToExcelAPI(employees, filters);
    downloadExcel(blob, filename);
    
    return {
      success: true,
      format: "excel",
      count: employees.length,
      filename: `${filename}.xlsx`,
    };
  } catch (err) {
    console.error("Excel export failed, falling back to CSV:", err);
    
    // Fallback to CSV export
    const csvContent = generateCSV(employees);
    downloadCSV(csvContent, filename);
    
    return {
      success: true,
      format: "csv",
      count: employees.length,
      filename: `${filename}.csv`,
    };
  }
};
